import type { Nullable } from '@ats/interfaces';
import useApplyVideoObjectFit from '@hooks/ref/useApplyVideoObjectFit';
import type { BaseComponentProps } from '@interfaces/selection';
import { useAudioVideo } from '@providers/AudioVideoProvider';
import { useVideoInputs } from '@providers/DevicesProvider';
import { useLocalVideo } from '@providers/LocalVideoProvider';
import { useLogger } from '@providers/LoggerProvider';
import { useMeetingManager } from '@providers/MeetingProvider';
import type { FC, PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { Container } from './style';

const PreviewVideo: FC<PropsWithChildren<BaseComponentProps>> = (props) => {
  const logger = useLogger();
  const audioVideo = useAudioVideo();
  const { selectedDevice } = useVideoInputs();
  const videoEl = useRef<Nullable<HTMLVideoElement>>(null);
  const meetingManager = useMeetingManager();
  const { setIsVideoEnabled } = useLocalVideo();
  useApplyVideoObjectFit(videoEl);

  useEffect(() => {
    const videoElement = videoEl.current;

    return () => {
      if (videoElement) {
        audioVideo?.stopVideoPreviewForVideoInput(videoElement);
        audioVideo?.stopVideoInput();
        setIsVideoEnabled(false);
      }
    };
  }, [audioVideo]);

  useEffect(() => {
    async function startPreview(): Promise<void> {
      if (!audioVideo || !selectedDevice || !videoEl.current) return;

      try {
        await meetingManager.startVideoInputDevice(selectedDevice);
        audioVideo.startVideoPreviewForVideoInput(videoEl.current);
        setIsVideoEnabled(true);
      } catch (error) {
        logger.error('Failed to start video preview');
      }
    }

    startPreview();
  }, [audioVideo, selectedDevice]);

  return (
    <Container {...props}>
      <video ref={videoEl} className='ch-video' />
    </Container>
  );
};

export default PreviewVideo;
